import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import accountVerificationService from '../../services/accountVerificationService';
import VerificationStatisticsDashboard from './components/VerificationStatisticsDashboard';
import FailedConfirmationsTable from './components/FailedConfirmationsTable';
import ManualVerificationPanel from './components/ManualVerificationPanel';
import AuditLogPanel from './components/AuditLogPanel';

const AccountVerificationManagementCenter = () => {
  const [activeTab, setActiveTab] = useState('failed-confirmations');
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [globalFilter, setGlobalFilter] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    loadStatistics();
  }, [refreshTrigger]);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      setError('');
      const stats = await accountVerificationService?.getVerificationStatistics();
      setStatistics(stats);
    } catch (err) {
      setError(err?.message || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const tabs = [
    {
      id: 'failed-confirmations',
      label: 'Failed Confirmations',
      icon: 'AlertCircle',
      badge: statistics?.failedConfirmations || 0
    },
    {
      id: 'manual-verification',
      label: 'Manual Verification',
      icon: 'UserCheck',
      badge: statistics?.pendingVerifications || 0
    },
    {
      id: 'audit-log',
      label: 'Audit Trail',
      icon: 'FileText',
      badge: null
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <GlobalFilterBar onFilterChange={setGlobalFilter} />
      <ComplianceAlertBanner />

      <main className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Account Verification Management Center
            </h1>
            <p className="text-muted-foreground">
              Monitor failed confirmations, manually verify accounts, and resend verification emails with audit logging
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleRefresh}>
              <Icon name="RefreshCw" size={16} className="mr-2" />
              Refresh
            </Button>
            <Button>
              <Icon name="Download" size={16} className="mr-2" />
              Export Report
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={20} className="text-error" />
              <p className="text-error font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Statistics Dashboard (4 cols) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
          <div className="lg:col-span-4">
            <VerificationStatisticsDashboard 
              statistics={statistics} 
              loading={loading}
              onRefresh={handleRefresh}
            />
          </div>

          {/* Main Management Panel (8 cols) */}
          <div className="lg:col-span-8">
            <div className="bg-card rounded-lg shadow-sm border border-border">
              {/* Tabs */}
              <div className="border-b border-border">
                <div className="flex overflow-x-auto">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                        activeTab === tab?.id
                          ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }`}
                    >
                      <Icon name={tab?.icon} size={18} />
                      <span>{tab?.label}</span>
                      {tab?.badge !== null && tab?.badge > 0 && (
                        <span className="ml-2 px-2 py-0.5 bg-error text-white text-xs font-bold rounded-full">
                          {tab?.badge}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {activeTab === 'failed-confirmations' && (
                  <FailedConfirmationsTable 
                    onActionComplete={handleRefresh}
                    refreshTrigger={refreshTrigger}
                  />
                )}
                {activeTab === 'manual-verification' && (
                  <ManualVerificationPanel 
                    onVerificationComplete={handleRefresh}
                    refreshTrigger={refreshTrigger}
                  />
                )}
                {activeTab === 'audit-log' && (
                  <AuditLogPanel refreshTrigger={refreshTrigger} />
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AccountVerificationManagementCenter;
